declare const I18n;

import React, { useMemo } from "react";
import "@/components/Chart/chart"; // ChartJS.register(...)
import { Bar } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";

export type LearnersInterestItem = {
  label: string;
  value: number; // raw units (NOT percent)
  color?: string;
};

type LearnersInterestsChartProps = {
  items: LearnersInterestItem[];
  height?: number; // px
};

const DEFAULT_COLORS = ["#8DC863", "#E60B80", "#FAAE1A","#42C0C6", "#CAB2E0"];


const measureTextWidth = (text: string, font: string) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

const LearnersInterestsChart: React.FC<LearnersInterestsChartProps> = ({
  items,
  height = 550,
}) => {
  const { itemsWithPercent, total } = useMemo(() => {
    const safeItems = (items ?? []).map((i) => ({
      ...i,
      value: Number.isFinite(i.value) ? i.value : 0,
    }));

    const sum = safeItems.reduce((acc, i) => acc + i.value, 0);

    const withPercent = safeItems.map((i) => ({
      ...i,
      percent: sum > 0 ? Math.round((i.value / sum) * 100) : 0,
    }));

    return { itemsWithPercent: withPercent, total: sum };
  }, [items]);

  const leftPadding = useMemo(() => {
    if (typeof window === "undefined") return 200; 

    const font = "400 25px"; 

    const longest = itemsWithPercent.reduce((max, item) => {
      return Math.max(
        max,
        measureTextWidth(item.label, font)
      ) + 16;
    }, 0);

    return Math.ceil(longest) + 10; // +10px breathing room
  }, [itemsWithPercent]);

  const data: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels: itemsWithPercent.map((i) => i.label),
      datasets: [
        {
          // Bars represent percent (0–100)
          data: itemsWithPercent.map((i) => i.value),  
          values: itemsWithPercent.map((i) => i.value),
          backgroundColor: itemsWithPercent.map(
            (i, idx) => i.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
          ),
          barThickness: 18,
          maxBarThickness: 20,
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    }),
    [itemsWithPercent]
  );

  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",

      layout: {
        padding: {
          left: leftPadding,
          right: 16,
        },
      },

      plugins: {
        legend: { display: false },

        barRowLabels: {
          items: itemsWithPercent,
        } as any,

        tooltip: {
          enabled: true,
          displayColors: true,
          callbacks: {
            label: (ctx) => {
              const value = Number(ctx.parsed.x)
              return `${value}`;
            },
          },
        },

      },

      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            display: false,
          },
          grid: { color: "rgba(255,255,255,0.1)" },
        },

        y: {
          ticks: {
            color: "#ffffff",
            font: { size: 14, weight: "400" },
            callback: (_tickValue, index) => {
              const item = itemsWithPercent[index];
              return item ? String(item.value) : "";
            },
          },
          grid: { display: false },
        },
      },
    }),
    [itemsWithPercent]
  );

  if (!itemsWithPercent.length || total <= 0) {
    return (
      <div className="learners-experience-level-chart-container">
        <h2 style={{ marginBottom: "2.5rem" }}>{I18n.t("views.learners.index.charts.your_learners_interest")}</h2>
        <div style={{ height, display: "flex", alignItems: "center" }}>
          <span style={{ color: "#ffffff", opacity: 0.7 }}>
            No data available.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="learners-experience-level-chart-container">
      <h2 style={{ marginBottom: "2.5rem" }}>{I18n.t("views.learners.index.charts.your_learners_interests")}</h2>
      <div style={{ height }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default LearnersInterestsChart;
